/**
 * Componente de zona de upload com drag & drop
 * 
 * Permite que usuários façam upload de arquivos arrastando para a área
 * ou clicando para selecionar arquivos do sistema
 */

import { useState, useRef, DragEvent } from 'react';
import { Upload, FileImage, FileVideo, FileAudio, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import {
  ACCEPTED_FILE_EXTENSIONS,
  MediaFileType,
  formatBytes,
  MEDIA_SIZE_LIMITS,
} from '@/types/media';

const logger = createLogger('MediaUploadZone');

interface MediaUploadZoneProps {
  onUpload: (file: File) => void;
  isUploading?: boolean;
  disabled?: boolean;
  className?: string;
}

export function MediaUploadZone({
  onUpload,
  isUploading = false,
  disabled = false,
  className,
}: MediaUploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Gerar accept string para input file
  const acceptString = Object.values(ACCEPTED_FILE_EXTENSIONS).flat().join(',');

  // =====================================================
  // HANDLERS DE DRAG & DROP
  // =====================================================

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    
    if (files.length === 0) {
      toast.error('Nenhum arquivo selecionado');
      return;
    }

    if (files.length > 1) {
      toast.error('Por favor, envie um arquivo por vez');
      return;
    }

    const file = files[0];
    logger.debug('Arquivo arrastado', { name: file.name, size: file.size });
    onUpload(file);
  };

  // =====================================================
  // HANDLER DE SELEÇÃO DE ARQUIVO
  // =====================================================

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    logger.debug('Arquivo selecionado', { name: file.name, size: file.size });
    onUpload(file);

    // Limpar input para permitir re-upload do mesmo arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div
      className={cn(
        'relative border-2 border-dashed rounded-lg p-8 transition-all duration-200',
        isDragging && 'border-primary bg-primary/5 scale-[1.02]',
        !isDragging && 'border-border hover:border-primary/50 hover:bg-accent/50',
        (disabled || isUploading) && 'opacity-50 cursor-not-allowed',
        !disabled && !isUploading && 'cursor-pointer',
        className
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptString}
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      <div className="flex flex-col items-center justify-center gap-4">
        {/* Ícone de upload */}
        <div
          className={cn(
            'rounded-full p-4 transition-all',
            isDragging ? 'bg-primary text-primary-foreground scale-110' : 'bg-muted text-muted-foreground'
          )}
        >
          <Upload className={cn('h-8 w-8', isUploading && 'animate-pulse')} />
        </div>

        {/* Texto principal */}
        <div className="text-center space-y-2">
          <p className="text-sm font-medium">
            {isUploading
              ? 'Enviando arquivo...'
              : isDragging
              ? 'Solte o arquivo aqui'
              : 'Arraste um arquivo ou clique para selecionar'}
          </p>
          
          {!isUploading && !isDragging && (
            <p className="text-xs text-muted-foreground">
              Formatos suportados: imagens, vídeos, áudios e documentos
            </p>
          )}
        </div>

        {/* Botão de upload (opcional) */}
        {!isUploading && !isDragging && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            Selecionar Arquivo
          </Button>
        )}

        {/* Informações de limites */}
        {!isUploading && !isDragging && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t w-full max-w-md text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <FileImage className="h-4 w-4" />
              <span>Imagens: até {formatBytes(MEDIA_SIZE_LIMITS.image)}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileVideo className="h-4 w-4" />
              <span>Vídeos: até {formatBytes(MEDIA_SIZE_LIMITS.video)}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileAudio className="h-4 w-4" />
              <span>Áudios: até {formatBytes(MEDIA_SIZE_LIMITS.audio)}</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Documentos: até {formatBytes(MEDIA_SIZE_LIMITS.document)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
